import cn from 'classnames';
import Image from 'next/image';
import { useState } from 'react';

import { Button } from '../Button';
import { Link } from '../Link';
import { VideoPlayer } from '../VideoPlayer';
import s from './ChristofariBlocks.module.scss';

interface BenefitsProps {
  title: string;
  footerText: string;
  items: {
    className: string;
    title: string;
    desc: string;
  }[];
}
export function Benefits({ title, footerText, items }: BenefitsProps) {
  return (
    <div className={s.chrisPreim}>
      <div className={s.chrisPreimBg} />
      <div className={s.chrisContainer}>
        <div className={s.chrisRow}>
          <h2
            className={s.chrisPreimTitle}
            dangerouslySetInnerHTML={{ __html: title }}
          />
          <div className={s.chrisPreimCol}>
            {items.map((item, index) => {
              return (
                <div className={s.chrisPreimBox} key={index}>
                  <h4
                    className={cn(s.chrisPreimBoxTitle, item.className)}
                    dangerouslySetInnerHTML={{ __html: item.title }}
                  />
                  <p
                    className={s.chrisPreimBoxDesc}
                    dangerouslySetInnerHTML={{ __html: item.desc }}
                  />
                </div>
              );
            })}
          </div>
          <h2
            className={cn(s.chrisPreimTitle, s.chrisPreimTitleBottom)}
            dangerouslySetInnerHTML={{ __html: footerText }}
          />
        </div>
      </div>
    </div>
  );
}

export function Companies() {
  return (
    <div className={s.chrisCompanies}>
      <div className={s.chrisContainer}>
        <div className={s.chrisCompaniesSlider}>
          <Image
            src="https://cdn.cloud.ru/backend/images/christofari/comp-logo-keras.png"
            className={s.chrisCompaniesLogo}
            alt="Company Logo"
            data-qa="company logo"
            width={190}
            height={40}
          />
          <Image
            src="/svg/pages/christofari/comp-logo-tensor.svg"
            className={s.chrisCompaniesLogo}
            alt="Company Logo"
            data-qa="company logo"
            width={190}
            height={40}
          />
          <Image
            src="/svg/pages/christofari/comp-logo-pyt.svg"
            className={s.chrisCompaniesLogo}
            alt="Company Logo"
            data-qa="company logo"
            width={190}
            height={40}
          />
          <Image
            src="/svg/pages/christofari/comp-logo-nav.svg"
            className={s.chrisCompaniesLogo}
            alt="Company Logo"
            data-qa="company logo"
            width={190}
            height={40}
          />
        </div>
      </div>
    </div>
  );
}

interface HeaderProps {
  title: string;
  subtitle: string;
  text: string;
  buttonText: string;
  videoTitle: string;
  videoLink: string;
  gtmButtonStyle: string;
  variant: 'christofari' | 'christofari-neo';
  onClick: any;
}

export function Header({
  title,
  subtitle,
  text,
  buttonText,
  videoTitle,
  videoLink,
  gtmButtonStyle,
  variant,
  onClick,
}: HeaderProps) {
  const [videoIsOpen, setVideoIsOpen] = useState(false);

  return (
    <section className={s.chrisHeader}>
      <div className={s.chrisContainer}>
        <div className={s.chrisRow}>
          <div className={s.chrisHeaderMain}>
            <span className={s.chrisHeaderSubtitle}>{subtitle}</span>
            <h1
              className={cn(s.chrisHeaderTitle, {
                [s.chrisHeaderTitleNeo]: variant === 'christofari-neo',
              })}
            >
              {title}
            </h1>
            <span className={s.chrisHeaderText}>{text}</span>

            <Button
              onClick={onClick}
              aria-label="Ml Space Form"
              className={cn(
                s.chrisHeaderBtn,
                s.chrisHeaderBtnMb154,
                gtmButtonStyle,
              )}
              id={`christofari-header-link-${title}`}
              analyticsOptions={{
                action: 'click',
                clickZone: 'header',
                clickElement: 'textclick',
                clickContent: title,
                uniqueId: `christofari-header-link-${title}`,
                transitionType: 'inside-link',
                clickZoneCategory: 'site-body',
              }}
            >
              {buttonText}
            </Button>
          </div>
        </div>
      </div>
      <div className={s.chrisHeaderFooter}>
        <div className={s.chrisContainer}>
          <div className={s.chrisHeaderFooterRow}>
            <div className={s.chrisHeaderInBox}>
              <h4 className={s.chrisHeaderFooterTitle}>{videoTitle}</h4>
              <span
                className={s.chrisHeaderFooterLink}
                onClick={() => setVideoIsOpen(true)}
              >
                {videoLink}
              </span>
            </div>
            <div
              className={cn(s.chrisHeaderInImg, {
                [s.chrisHeaderInImgNeo]: variant === 'christofari-neo',
              })}
            />
          </div>
        </div>
      </div>

      {videoIsOpen && (
        <VideoPlayer
          className={s.chrisHeaderVideo}
          videoClassName={s.chrisHeaderPlayer}
          link="https://www.youtube.com/embed/dxqckgzfsA8"
        >
          <button
            className={cn(s.chrisHeaderClose, 'btn-green')}
            onClick={() => setVideoIsOpen(false)}
          />
        </VideoPlayer>
      )}
    </section>
  );
}

interface PowerBlockProps {
  rate: string;
  title: string;
}

export function PowerBlock({ rate, title }: PowerBlockProps) {
  return (
    <div className={s.chrisPetaflops}>
      <div className={s.chrisContainer}>
        <div className={s.chrisRow}>
          <div className={s.chrisPetaflopsBox}>
            <span className={s.chrisPetaflopsRate}>{rate}</span>
            <span className={s.chrisPetaflopsTitle}>{title}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Switcher({
  version,
}: {
  version: 'christofari' | 'christofari-neo';
}) {
  return (
    <div className={s.switcher}>
      {version === 'christofari' && (
        <>
          <div className={cn(s.switcherItem, s.switcherItemActive)}>
            Christofari
          </div>
          <Link
            className={s.switcherItem}
            href="/mlspace/christofari-neo"
            id="christofari-switcher-link-neo"
            analyticsOptions={{
              action: '/mlspace/christofari-neo',
              clickZone: 'body',
              clickElement: 'textclick',
              clickContent: 'Christofari Neo',
              uniqueId: 'christofari-switcher-link-neo',
              transitionType: 'inside-link',
              clickZoneCategory: 'site-body',
              transitionUrl: '/mlspace/christofari-neo',
              transitionTypeMetrics: 'internal',
            }}
          >
            Christofari&nbsp;
            <span className={s.switcherItemLight}>Neo</span>
          </Link>
        </>
      )}
      {version === 'christofari-neo' && (
        <>
          <Link
            href="/mlspace/christofari"
            className={s.switcherItem}
            id="christofari-switcher-link"
            analyticsOptions={{
              action: '/mlspace/christofari',
              clickZone: 'body',
              clickElement: 'textclick',
              clickContent: 'Christofari',
              uniqueId: 'christofari-switcher-link',
              transitionType: 'inside-link',
              clickZoneCategory: 'site-body',
              transitionUrl: '/mlspace/christofari',
              transitionTypeMetrics: 'internal',
            }}
          >
            Christofari
          </Link>
          <div className={cn(s.switcherItem, s.switcherItemActive)}>
            Christofari&nbsp;
            <span className={s.switcherItemLight}>Neo</span>
          </div>
        </>
      )}
    </div>
  );
}

export function Welcome({ title }: { title: string }) {
  return (
    <div className={s.chrisWelcome}>
      <div className={s.chrisContainer}>
        <h2
          className={s.chrisWelcomeTitle}
          dangerouslySetInnerHTML={{ __html: title }}
        />
      </div>
    </div>
  );
}
